import { SubscriptionLike } from 'rxjs'

import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Inject, PLATFORM_ID } from '@angular/core'
import { DOCUMENT, isPlatformBrowser } from '@angular/common'
import { Router, ActivatedRoute } from '@angular/router'
import { FormControl } from '@angular/forms'

@Component({
    selector: 'app-pagination',
    templateUrl: './pagination.component.html',
    styleUrls: ['./pagination.component.scss'],
})
export class PaginationComponent implements OnInit, OnDestroy {

    @Output()
    public pageChanged = new EventEmitter<number>()

    /**
     * The total number of pages for the current refinements.
     */
    @Input()
    public pagesLength: number
    private internalCurrentPageIndex: number

    /**
     * The current page starting from 0, as used internally by Algolia
     */
    @Input()
    public set currentPageIndex(i: number) {
        this.internalCurrentPageIndex = i
        if (this.currentPageControl) {
            this.currentPageControl.setValue(i)
        }
    }
    public get currentPageIndex() {
        return this.internalCurrentPageIndex
    }

    /**
     * Form control holding the current page starting from 1
     */
    @Input()
    public currentPageControl?: FormControl<number>

    /**
     * A list of available page numbers starting at 1
     */
    @Input()
    public selectablePages: string[]

    private currentPageControlSubscription: SubscriptionLike

    constructor(
        @Inject(DOCUMENT)
        private doc: Document,
        @Inject(PLATFORM_ID)
        private platformId: string,
        private router: Router,
        private route: ActivatedRoute,
    ) {}

    public ngOnInit(): void {
        if (! isPlatformBrowser(this.platformId)) {
            if (this.currentPageIndex !== this.selectablePages.length) {
                const nextLink: HTMLLinkElement = this.doc.createElement('link')
                nextLink.setAttribute('rel', 'next')
                nextLink.setAttribute('href', this.getTreeForPage(this.currentPageIndex + 2))
                this.doc.head.appendChild(nextLink)
            }

            if (this.currentPageIndex !== 0) {
                const prevLink: HTMLLinkElement = this.doc.createElement('link')
                prevLink.setAttribute('rel', 'prev')
                prevLink.setAttribute('href', this.getTreeForPage(this.currentPageIndex))
                this.doc.head.appendChild(prevLink)
            }
        }

        if (! this.currentPageControl) {
            this.currentPageControl = new FormControl(this.currentPageIndex, {
                nonNullable: true,
            })
        }

        this.currentPageControlSubscription = this.currentPageControl.valueChanges
            .subscribe((value) => this.pageChanged.emit(value))
    }

    public ngOnDestroy(): void {
        this.currentPageControlSubscription?.unsubscribe()
    }

    public prev(): void {
        if (this.currentPageControl) {
            this.patchFormValue(this.currentPageControl.value - 1)
        }
    }

    public next(): void {
        if (this.currentPageControl) {
            this.patchFormValue(this.currentPageControl.value + 1)
        }
    }

    public getTreeForPage(page: number): string {
        return this.router.createUrlTree(
            [],
            {
                relativeTo: this.route,
                queryParams: {
                    page: page === 1 ? undefined : page,
                },
                queryParamsHandling: 'merge', // remove to replace all query params by provided
            },
        ).toString()
    }

    private patchFormValue(pageIndex: number): void {
        this.currentPageControl?.patchValue(pageIndex)
    }

}
